//#region IMPORT
import React, { useState, useEffect } from 'react';
import M_WOS_BASIC_01 from './M_WOS_BASIC_01';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavBarInfoActions } from '../../../Reducer/NavBarInfoReducer';
import { BottomSheetActions } from '../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { WorkingCheckActions } from '../../../Reducer/WorkingCheckReducer';
import useFcmHandler from '../../../Hooks/useFcmHandler';
import {
  useSearch3,
  useSave1,
  useSave2,
  useSearch5,
} from './M_WOS_BASIC_01_Query';
import { useSave } from '../../../Hooks/useSave';
import useFileUpload from '../../../Hooks/useFileUpload';
import { WOS_BASIC_01_Schema } from './M_WOS_BASIC_01_Schema';
import { useAppNavigate } from '../../../Hooks/back/useAppNavigate';
import { useBackStack } from '../../../Hooks/back/useBackStack';
import Global from '../../../Services/Common/Global';
//#endregion

const INPUTS = {
  workStopCd: '',
  depCd: '',
  stopAuth: '',
  stopComment: '',
  stopImagePath: '',
  userId: '',
};

const FCM_PAYLOAD_BASE = {
  status: '작업중지',
  codeGroup: 'AL_006_001',
  title: '작업중지',
  route: '/M_WorkStop',
};

const STOP_AUTH_LABEL = {
  '0': '근로자',
  '1': '관리자(본부)',
  '2': '관리자(사업주)',
};

const logWorkStopSubmitContext = ({
  userInfo,
  stopAuth,
  targetDepCd,
  selectedTargets,
}) => {
  const emtYn = userInfo?.emtYn ?? 'N';
  const targetDepartments =
    stopAuth === '0'
      ? [
          {
            depCd: userInfo?.depCd ?? '',
            depNm: userInfo?.depNm ?? '',
          },
        ]
      : (selectedTargets || []).map((t) => ({
          depCd: t.depCd ?? '',
          depNm: t.depNm ?? t.selectedPath ?? '',
        }));

  console.log('[작업중지 작성]', {
    stopAuth,
    stopAuthLabel: STOP_AUTH_LABEL[stopAuth] ?? stopAuth,
    authCd: userInfo?.authCd ?? '',
    posiNm: userInfo?.posiNm ?? '',
    emtYn,
    isEmergencyTel: String(emtYn).toUpperCase() === 'Y',
    targetDepCd,
    targetDepartments,
  });
};

export default function M_WOS_BASIC_01F() {
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const [workStopInputs, setWorkStopInputs] = useState(INPUTS);
  const [errors, setErrors] = useState({});
  const [selectedTargets, setSelectedTargets] = useState([]);
  const sendFcm = useFcmHandler();
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const [masterData, setMasterData] = useState([]);
  const workStopUpload = useFileUpload({
    oldFilePathField: 'image',
    newFileNameField: 'saveName',
    maxCount: 5,
    getTargetPath: () => `WorkStop/${userInfo.custCd}/WOS_BASIC_01`,
  });

  useEffect(() => {
    const data = location.state?.data;
    if (!data) return;

    const isInsertFromInstruction = location.state?.fromInstruction === true;

    setWorkStopInputs((prev) => ({
      ...prev,
      workStopCd: isInsertFromInstruction ? '' : (data.workStopCd ?? ''),
      depCd: data.depCd ?? '',
      stopAuth: data.stopAuth ?? '',
      stopComment: data.stopComment ?? '',
      stopImagePath: data.stopImagePath ?? '',
    }));

    const paths = data.stopImagePath
      ? data.stopImagePath
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean)
      : [];

    if (paths.length > 0) {
      workStopUpload.setPreviewList(
        paths.map((path) => {
          const fileName = path.split('/').pop() || 'image';
          return {
            preview: `${Global.file.useUrl}files/${path}`,
            image: path,
            saveName: fileName,
            fileName,
            fileType: '',
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.data?.workStopCd]);

  useBackStack({
    type: 'sheet',
    enabled: !bottomSheetState.bottom,
    onBack: () =>
      dispatch(BottomSheetActions.OpenBottomSheet({ type: 'backClickWos' })),
  });

  useEffect(() => {
    Init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Init = () => {
    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('작업 중지'));
  };

  //#region 쿼리
  const { runAsync: Search3Async } = useSearch3({
    onSuccess: (rows) => {
      if (rows?.[0]?.type === 'Error') {
        dispatch(
          showSnackbar({
            message: '인원 조회 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
      }
    },
  });
  useSearch5({
    onSuccess: (rows) => {
      const processed = (rows || []).map((item) => {
        const path = [
          item.depNm1,
          item.depNm2,
          item.depNm3,
          item.depNm4,
        ].filter(Boolean);
        return {
          ...item,
          pathArray: path,
          depth: path.length,
        };
      });
      setMasterData(processed);
    },
  });
  const { runAsync: Save1Async } = useSave1();
  const { runAsync: Save2Async } = useSave2();

  const getStopAuth = () => {
    if (userInfo.authCd === 'HEAD_MANAGER') return '1';
    if (userInfo.authCd === 'BUSINESS_OWNER') return '2';
    return '0';
  };

  const getTargetDepCd = (stopAuth) => {
    if (stopAuth === '0') return userInfo.depCd ?? '';
    const depCds = selectedTargets.map((t) => t.depCd).filter(Boolean);
    return depCds.join(',');
  };

  const { run: Save1 } = useSave({
    menuId: 'M_WOS_BASIC_01',
    action: 'SAVE_WORK_STOP',
    schema: WOS_BASIC_01_Schema,
    saveData: () => {
      const stopAuth = getStopAuth();
      const isInsertFromInstruction = location.state?.fromInstruction === true;
      return {
        workStopCd: isInsertFromInstruction
          ? ''
          : (workStopInputs.workStopCd ?? ''),
        depCd: userInfo.depCd ?? '',
        targetDepCd: getTargetDepCd(stopAuth),
        emtYn: userInfo.emtYn ?? 'N',
        stopAuth,
        stopComment: workStopInputs.stopComment ?? '',
        stopImagePath: '',
      };
    },
    requestSave: async (rows) => {
      const row = rows[0] ?? {};
      const stopAuth = getStopAuth();
      const targetDepCd = getTargetDepCd(stopAuth);

      logWorkStopSubmitContext({
        userInfo,
        stopAuth,
        targetDepCd,
        selectedTargets,
      });

      const res = await Save1Async({
        row: { ...row, stopImagePath: '' },
        userInfo,
      });
      const workStopCd = res?.[0]?.workStopCd ?? res?.workStopCd;
      if (!workStopCd) return res;

      setWorkStopInputs((prev) => ({ ...prev, workStopCd }));

      let stopImagePath = '';
      const validImages = workStopUpload.previewList.filter(
        (img) => !img.deleted
      );
      if (validImages.length > 0) {
        const existingPaths = validImages
          .filter((img) => img.image && !img.previewFilePath)
          .map((img) => img.image);
        const uploadedPaths =
          (await workStopUpload.SaveLogicFile(
            validImages.filter((img) => img.previewFilePath)
          )) || '';
        const combined = [
          ...existingPaths,
          ...(uploadedPaths
            ? uploadedPaths.split(',').map((p) => p.trim())
            : []),
        ].filter(Boolean);
        stopImagePath = combined.join(',');
        await Save1Async({
          row: {
            ...row,
            workStopCd,
            stopImagePath,
          },
          userInfo,
        });
      }

      dispatch(WorkingCheckActions.setStopWorking(1));
      dispatch(WorkingCheckActions.setWorkStopCd(workStopCd));
      if (
        userInfo.authCd === 'BUSINESS_OWNER' ||
        userInfo.authCd === 'HEAD_MANAGER'
      ) {
        navigate('/Home');
      } else {
        navigate(`/M_WorkStop/${workStopCd}`);
      }

      void SaveLogic2(workStopCd, stopAuth);
      return res;
    },
  });
  //#endregion

  const sendWorkStopFcm = async (workStopCd, memberList) => {
    const targetUserIds = [
      ...new Set(
        (memberList || [])
          .map((member) => member.id ?? member.userId)
          .filter(Boolean)
      ),
    ];
    if (!targetUserIds.length) return;

    await sendFcm({
      targetUserIds,
      body: `${userInfo.userNm}님이 작업 중지를 요청했습니다.`,
      ...FCM_PAYLOAD_BASE,
      responseData: workStopCd,
    });
  };

  const isExternalUser = userInfo?.internalYn === '1';

  // U1 → (navigate) → 백그라운드: R3 → U2(배열) → FCM
  const SaveLogic2 = async (workStopCd, stopAuth) => {
    try {
      const targetDepCd = getTargetDepCd(stopAuth);
      if (!targetDepCd && !isExternalUser) return;

      const members = await Search3Async({
        stopAuth,
        depCd: isExternalUser ? '' : targetDepCd,
        userInfo,
      });
      const memberList = Array.isArray(members)
        ? members
        : (members?.data ?? members?.Data ?? []);

      console.log('[작업중지 작성] 알림 대상 조회(R3)', {
        stopAuth,
        stopAuthLabel: STOP_AUTH_LABEL[stopAuth] ?? stopAuth,
        targetDepCd,
        memberCount: memberList.length,
        members: memberList.map((m) => ({
          id: m.id,
          name: m.name,
          depNmLast: m.depNmLast,
        })),
      });

      const items = memberList
        .filter((m) => m.id)
        .map((m) => ({
          workStopCd,
          stopId: m.id,
        }));

      if (items.length === 0) return;

      const result = await Save2Async({ items, userInfo });
      const failedItems = (result?.data || []).filter(
        (item) => !item?.isSuccess
      );
      if (failedItems.length > 0) {
        throw new Error(
          failedItems[0]?.message || '작업중지 멤버 저장 중 오류가 발생했습니다.'
        );
      }

      void sendWorkStopFcm(workStopCd, memberList).catch((e) =>
        console.error('작업중지 FCM 발송 실패:', e)
      );
    } catch (e) {
      console.error('SaveLogic2 Error:', e);
      dispatch(
        showSnackbar({
          message: e?.message || '작업중지 멤버 저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  return (
    <div className="wrap">
      <M_WOS_BASIC_01
        location={location}
        workStopUpload={workStopUpload}
        selectedTargets={selectedTargets}
        setSelectedTargets={setSelectedTargets}
        errors={errors}
        setErrors={setErrors}
        workStopInputs={workStopInputs}
        setWorkStopInputs={setWorkStopInputs}
        SaveLogic1={Save1}
        masterData={masterData}
      />
    </div>
  );
}
